import { SoundOverlayAdaptor } from '../types/overlay-adaptors';
import { StandardAudio } from '../types/media-adaptors';
import { ConvexHttpClient } from "convex/browser";
import { api } from '../../../../convex/_generated/api';

/**
 * Creates a Convex-based audio adaptor for Music or SFX
 * This adaptor queries the Convex audioLibrary table
 */
export const createConvexAudioAdaptor = (
  audioType: "music" | "sfx",
  convexUrl: string
): SoundOverlayAdaptor => {
  const client = new ConvexHttpClient(convexUrl);
  const displayName = audioType === "music" ? "Music" : "SFX";

  return {
    name: `convex-${audioType}`,
    displayName,
    description: `${displayName} library from Convex storage`,
    requiresAuth: false,

    search: async (params) => {
      try {
        const result = await client.query(api.audioLibrary.listAudio, {
          audioType,
          searchQuery: params.query,
          tags: undefined, // Can be extended to support tag filtering
          page: params.page,
          perPage: params.perPage,
        });

        // Map Convex audio items to StandardAudio format
        const items: StandardAudio[] = await Promise.all(
          result.items.map(async (item) => {
            // Get the storage URL for each audio file
            const audioUrl = await client.query(api.audioLibrary.getAudioUrl, {
              storageId: item.storageId,
            });

            return {
              id: item._id,
              title: item.name,
              artist: item.artist || (audioType === "sfx" ? "SFX Library" : "Music Library"),
              duration: item.duration || 0,
              file: audioUrl || "",
              // Store tags in a custom field (extending StandardAudio)
              tags: item.tags || [],
            } as StandardAudio & { tags?: string[] };
          })
        );

        return {
          items,
          totalCount: result.totalCount,
          hasMore: result.hasMore,
        };
      } catch (error) {
        console.error(`Error searching ${audioType} audio:`, error);
        return {
          items: [],
          totalCount: 0,
          hasMore: false,
        };
      }
    },

    getAudioUrl: (audio: StandardAudio) => {
      // The file URL is already set in the search result
      return audio.file;
    },
  };
};

/**
 * Create a Music-only adaptor
 */
export const createConvexMusicAdaptor = (convexUrl?: string): SoundOverlayAdaptor => {
  const url = convexUrl || (import.meta.env.VITE_CONVEX_URL as string);
  if (!url) {
    throw new Error("Convex URL is required. Set VITE_CONVEX_URL environment variable.");
  }
  return createConvexAudioAdaptor("music", url);
};

/**
 * Create an SFX-only adaptor
 */
export const createConvexSfxAdaptor = (convexUrl?: string): SoundOverlayAdaptor => {
  const url = convexUrl || (import.meta.env.VITE_CONVEX_URL as string);
  if (!url) {
    throw new Error("Convex URL is required. Set VITE_CONVEX_URL environment variable.");
  }
  return createConvexAudioAdaptor("sfx", url);
};

